import { CallingStatusFilter, CONTACT_STATUS_CODE_MAPPINGS } from '@/types';
import {
  CALLING_STATUS_OPTIONS_IDS,
} from '@/constants/filterCriteria/callingStatusOptions';
import { COMMS_PREFERENCE_OPTIONS } from '@/constants/filterCriteria/commsPreferences';
import { PARTICIPANT_STATUSES_WITH_IDS } from '@/constants/filterCriteria/eventParticipants';
import { FlatSearchTreeLeafNode } from '@components/LoadSearchTemplateDialog/flatSearchTree';
import AgeTFC from '@components/LoadSearchTemplateDialog/templateFormControls/AgeTFC';
import CallingPeriodQuantityTFC from '@components/LoadSearchTemplateDialog/templateFormControls/CallingPeriodQuantityTFC';
import DatePickerTFC from '@components/LoadSearchTemplateDialog/templateFormControls/DatePickerTFC';
import FormWithFetchAutocompleteByLabelTFC from '@components/LoadSearchTemplateDialog/templateFormControls/FetchAutocompleteByLabelTFC';
import PathwayStepsTFC from '@components/LoadSearchTemplateDialog/templateFormControls/PathwayStepsTFC';
import Sa1TFC from '@components/LoadSearchTemplateDialog/templateFormControls/Sa1TFC';
import SupporterStatusTFC from '@components/LoadSearchTemplateDialog/templateFormControls/SupporterStatusTFC';

import type {
  MappedTemplateFormControlMapDeps,
  TemplateFormControlMap,
} from '@components/LoadSearchTemplateDialog/mappedTemplateFormControl/types';

export function buildLabelsDatesAndMiscControls(
  deps: MappedTemplateFormControlMapDeps
): Partial<TemplateFormControlMap> {
  const {
    initialFlatSearchTreeNode,
    simpleInitialValue,
    callingPeriodQuantityInitialValue,
  } = deps;

  return {
    'callingStatus.callingStatusLabels': ({ onChildChange }) => (
      <FormWithFetchAutocompleteByLabelTFC
        label={'Add a calling status'}
        onChange={onChildChange}
        minLength={0}
        preLoadedOptions={CALLING_STATUS_OPTIONS_IDS}
        initialValue={simpleInitialValue as string[]}
      />
    ),
    'commsPreferences.commsPreferencesLabels': ({ onChildChange }) => (
      <FormWithFetchAutocompleteByLabelTFC
        label={'Add a communication setting'}
        onChange={onChildChange}
        minLength={0}
        preLoadedOptions={COMMS_PREFERENCE_OPTIONS}
        initialValue={simpleInitialValue as string[]}
      />
    ),
    'eventAttendance.statusIds': ({ onChildChange }) => (
      <FormWithFetchAutocompleteByLabelTFC
        label={'Participant status(es) (optional)'}
        onChange={onChildChange}
        minLength={0}
        preLoadedOptions={PARTICIPANT_STATUSES_WITH_IDS}
        isEmptyValid
        initialValue={simpleInitialValue as string[]}
      />
    ),
    'callingStatus.callingStatusDateRange.displayedStartDate': ({ onChildChange }) => (
      <DatePickerTFC
        label={'Contacted from date'}
        valueIfNull={null}
        onChange={onChildChange}
        initialValue={simpleInitialValue as string | null}
      />
    ),
    'callingStatus.callingStatusDateRange.displayedEndDate': ({ onChildChange }) => (
      <DatePickerTFC
        label={'Contacted to date'}
        valueIfNull={null}
        onChange={onChildChange}
        initialValue={simpleInitialValue as string | null}
      />
    ),
    'engagementHistory.activityDateRange.displayedStartDate': ({ onChildChange }) => (
      <DatePickerTFC
        label={'From date'}
        valueIfNull={null}
        onChange={onChildChange}
        initialValue={simpleInitialValue as string | null}
      />
    ),
    'engagementHistory.activityDateRange.displayedEndDate': ({ onChildChange }) => (
      <DatePickerTFC
        label={'To date'}
        valueIfNull={null}
        onChange={onChildChange}
        initialValue={simpleInitialValue as string | null}
      />
    ),
    'eventAttendance.eventDateRange.startDate': ({ onChildChange }) => (
      <DatePickerTFC
        label={'Attended from date'}
        valueIfNull={undefined}
        onChange={onChildChange}
        isEmptyValid
        initialValue={simpleInitialValue as string | null}
      />
    ),
    'eventAttendance.eventDateRange.endDate': ({ onChildChange }) => (
      <DatePickerTFC
        label={'Attended to date'}
        valueIfNull={undefined}
        onChange={onChildChange}
        isEmptyValid
        initialValue={simpleInitialValue as string | null}
      />
    ),
    supporterStatuses: ({ onChildChange }) => (
      <SupporterStatusTFC
        label={'Find a Rocket status'}
        onChange={onChildChange}
        minLength={0}
        initialValue={
          simpleInitialValue as unknown as (keyof typeof CONTACT_STATUS_CODE_MAPPINGS)[]
        }
      />
    ),
    pathwaySteps: ({ onChildChange }) => (
      <PathwayStepsTFC
        label={'Step(s)'}
        onChange={onChildChange}
        initialValue={simpleInitialValue as number[]}
      />
    ),
    age: ({ onChildChange }) => (
      <AgeTFC
        label={'Age'}
        onChange={onChildChange}
        initialValue={simpleInitialValue as number}
      />
    ),
    sa1: ({ onChildChange }) => (
      <Sa1TFC
        label={'Add 11 digit SA1(s)'}
        onChange={onChildChange}
        initialValue={simpleInitialValue as number[]}
      />
    ),
    'callingStatus.callingStatusDateRange.callingPeriodQuantity': ({ onChildChange }) => (
      <CallingPeriodQuantityTFC
        onChange={onChildChange}
        defaultCallingPeriodDetails={
          ((initialFlatSearchTreeNode as FlatSearchTreeLeafNode)?.filter as CallingStatusFilter)
            ?.value?.callingStatusDateRange?.callingPeriodDetails || 'weeks'
        }
        initialValue={callingPeriodQuantityInitialValue}
      />
    ),
  };
}
